import React, { useMemo, useState } from "react";
import { Popover, PopoverSurface, PopoverTrigger } from "@fluentui/react-components";
import styles from "../ResourcesPlanner.module.scss";
import { dayToIso } from "../engine/dayMath";
import { tasksInPeriod } from "../engine/rowModel";
import { useData, usePlannerEngine, useSummaryRange, useSummaryReady, useView } from "../plannerContext";
import { formatDateRange, pluralize } from "../../../components/resourceInsights/cardFormat";
import TasksAccordion from "../../../components/resourceInsights/TasksAccordion";
import TruncatedText from "../../../components/truncatedText/TruncatedText";

const COMPLETED_STATUS = 3;

/** The tasks in the way Resource Insights lists the tasks of a resource, with the names of what they belong to. */
function toAssignment(allocation, data, today) {
  return {
    id: allocation.taskId,
    name: allocation.name,
    refId: allocation.refId,
    status: allocation.status,
    priority: allocation.priority,
    progress: allocation.progress,
    isCritical: allocation.isCritical,
    isMilestone: allocation.isMilestone,
    isOverdue: allocation.status !== COMPLETED_STATUS && allocation.endDay < today,
    startDate: allocation.startDate,
    endDate: allocation.endDate,
    project: { id: allocation.projectId, name: data.projects[allocation.projectId]?.name },
    scope: { id: allocation.scopeId, name: data.scopes[allocation.scopeId]?.name },
    taskGroup: allocation.groupId ? { id: allocation.groupId, name: data.groups[allocation.groupId]?.name } : null,
    allocation: { startDate: allocation.startDate, endDate: allocation.endDate, hoursPerDay: allocation.hoursPerDay, totalHours: allocation.totalHours },
  };
}

/** What the popup lists, built only while it is open: the tasks grouped by project, scope and task group. */
function TasksList({ tasks, range, label, onDone }) {
  const { actions, today } = usePlannerEngine();
  const data = useData((state) => state.data);
  const showHours = useView((state) => state.showHours);

  const items = useMemo(() => tasks.map((allocation) => toAssignment(allocation, data, today)), [tasks, data, today]);

  return (
    <div className={styles.tasksList}>
      <div className={styles.tasksListHeader}>
        <strong>{pluralize(tasks.length, "task")}</strong>
        <TruncatedText text={label} />
        <em>{formatDateRange(dayToIso(range.startDay), dayToIso(range.endDay))}</em>
      </div>

      <div className={styles.tasksListBody}>
        <TasksAccordion
          tasks={items}
          showHours={showHours}
          onNavigate={(picked) => {
            onDone();
            actions.goToDate(picked.allocation.startDate);
          }}
          onEdit={(picked) => {
            onDone();
            actions.openEditTask(picked.id);
          }}
        />
      </div>
    </div>
  );
}

/**
 * How many tasks a row stands for in the period the planner summarises (the one above the left panel), shown on the
 * right of the row whether there are any or not. With tasks it opens the list of them, the way Resource Insights
 * lists the tasks of a resource.
 *
 * @param row  a resource, project, scope or task group row of the row model
 * @param label  what the row is called, as the heading of the list
 */
function TasksPill({ row, label }) {
  const range = useSummaryRange();
  const isReady = useSummaryReady(row.resourceId);
  const [open, setOpen] = useState(false);

  const tasks = useMemo(() => tasksInPeriod(row, range.startDay, range.endDay), [row, range]);
  const text = `${tasks.length} ${tasks.length === 1 ? "task" : "tasks"}`;

  // A long period is still being fetched, so what is counted so far would read too low.
  if (!isReady) return <span className={`${styles.tasksPill} ${styles.tasksPillEmpty}`} aria-busy="true">… tasks</span>;

  if (tasks.length === 0) return <span className={`${styles.tasksPill} ${styles.tasksPillEmpty}`}>{text}</span>;

  return (
    <Popover open={open} onOpenChange={(_, data) => setOpen(data.open)} positioning={{ position: "below", align: "start", autoSize: "height" }} trapFocus={false}>
      <PopoverTrigger disableButtonEnhancement>
        <button type="button" className={`${styles.tasksPill} ${open ? styles.tasksPillOpen : ""}`} aria-label={`${text} of ${label} in the period`} onClick={(event) => event.stopPropagation()}>
          {text}
        </button>
      </PopoverTrigger>

      <PopoverSurface className={styles.tasksSurface} onClick={(event) => event.stopPropagation()}>
        <TasksList tasks={tasks} range={range} label={label} onDone={() => setOpen(false)} />
      </PopoverSurface>
    </Popover>
  );
}

export default React.memo(TasksPill);
